import React from 'react';
import { useTranslation } from 'react-i18next';
import { UserTable } from '../../widgets/user-table';
import { CreateUserForm, EditUserForm } from '../../widgets/user-form';
import { AssignRoleDialog } from '../../widgets/user-roles';
import { ConfirmDialog } from '../../components/ui';
import { usePermissions } from '../../shared/lib/hooks/use-auth';
import { useDeleteUser } from '../../shared/lib/hooks/use-users';
import type { User } from '../../shared/types';

export const UsersPage: React.FC = () => {
  const { t } = useTranslation();
  const { canReadUsers } = usePermissions();
  const deleteUserMutation = useDeleteUser();

  // Modal states
  const [createUserOpen, setCreateUserOpen] = React.useState(false);
  const [editUserOpen, setEditUserOpen] = React.useState(false);
  const [assignRoleOpen, setAssignRoleOpen] = React.useState(false);
  const [selectedUserId, setSelectedUserId] = React.useState<string | null>(null);
  const [selectedUserForRole, setSelectedUserForRole] = React.useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedUserForDelete, setSelectedUserForDelete] = React.useState<User | null>(null);

  // Handlers
  const handleCreateUser = () => {
    setCreateUserOpen(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUserId(user.id);
    setEditUserOpen(true);
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUserForDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDeleteUser = async () => {
    if (!selectedUserForDelete) {
      return;
    }

    try {
      await deleteUserMutation.mutateAsync(selectedUserForDelete.id);
      setSelectedUserForDelete(null);
    } catch (error) {
      // Error notification is handled by the mutation hook.
      console.error('Failed to delete user:', error);
    }
  };

  const handleAssignRole = (user: User) => {
    setSelectedUserForRole(user);
    setAssignRoleOpen(true);
  };

  // If user doesn't have read permissions, show access denied
  if (!canReadUsers) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">
            {t('users.title')}
          </h1>
        </div>
        <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6 sm:p-8">
          <div className="text-center space-y-3 sm:space-y-4">
            <h3 className="text-base sm:text-lg font-semibold">{t('errors.forbidden')}</h3>
            <p className="text-muted-foreground text-sm sm:text-base">
              {t('errors.unauthorized')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="space-y-1 sm:space-y-2">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
          {t('users.title')}
        </h1>
        <p className="text-muted-foreground text-sm sm:text-base">
          {t('users.manageDescription', 'Manage system users and their permissions.')}
        </p>
      </div>

      <UserTable
        onCreateUser={handleCreateUser}
        onEditUser={handleEditUser}
        onDeleteUser={handleDeleteUser}
        onAssignRole={handleAssignRole}
      />

      {/* Create User Modal */}
      <CreateUserForm
        open={createUserOpen}
        onOpenChange={setCreateUserOpen}
      />

      {/* Edit User Modal */}
      <EditUserForm
        userId={selectedUserId}
        open={editUserOpen}
        onOpenChange={(open) => {
          setEditUserOpen(open);
          if (!open) {
            setSelectedUserId(null);
          }
        }}
      />

      {/* Assign Role Modal */}
      <AssignRoleDialog
        user={selectedUserForRole}
        open={assignRoleOpen}
        onOpenChange={(open) => {
          setAssignRoleOpen(open);
          if (!open) {
            setSelectedUserForRole(null);
          }
        }}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) {
            setSelectedUserForDelete(null);
          }
        }}
        title={t('users.deleteUser')}
        description={t('users.confirmDeleteUser')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="destructive"
        onConfirm={handleConfirmDeleteUser}
        loading={deleteUserMutation.isPending}
      />
    </div>
  );
};